import { useState } from "react";
import { Bot, Copy, ThumbsDown, ThumbsUp, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatMessageSources from "./chat-message-sources";
import { showToast } from "@/lib/toast";
import {
  citationNumberFromHref,
  handleCitationClick,
  linkifyCitations,
} from "@/lib/chat-citations";
import { tableComponents } from "@/lib/markdown-renderers";
import { submitChatFeedback, type ChatSourceOut } from "@/hooks/use-chat";

interface ChatMessageProps {
  // Necesitamos el id del mensaje para que las anchors `id="source-N-<msg>"`
  // no colisionen entre mensajes distintos.
  id?: string;
  role: "user" | "assistant";
  content: string;
  timestamp?: Date;
  sources?: ChatSourceOut[] | null;
  model?: string | null;
  /** id de la traza Langfuse → habilita el feedback 👍/👎. */
  traceId?: string | null;
}

/** Etiqueta corta y legible del modelo (claude-sonnet-4-6 → "Sonnet"). */
function modelLabel(model?: string | null): string | null {
  if (!model) return null;
  const m = model.toLowerCase();
  if (m.includes("sonnet")) return "Sonnet";
  if (m.includes("haiku")) return "Haiku";
  if (m.includes("opus")) return "Opus";
  if (m.includes("gpt")) return model;
  return model;
}

export default function ChatMessage({
  id,
  role,
  content,
  timestamp,
  sources,
  model,
  traceId,
}: ChatMessageProps) {
  const isUser = role === "user";
  const [vote, setVote] = useState<null | "up" | "down">(null);
  const [draft, setDraft] = useState("");
  const [sent, setSent] = useState(false);

  const send = () => {
    if (!traceId || vote === null) return;
    const positive = vote === "up";
    setSent(true);
    submitChatFeedback(traceId, positive, draft)
      .then(() =>
        showToast.success(
          positive ? "¡Gracias por tu valoración!" : "¡Gracias! Lo tendremos en cuenta."
        )
      )
      .catch(() => {
        setSent(false);
        showToast.error("No se pudo enviar la valoración");
      });
  };
  const messageId = id || `${timestamp?.getTime() ?? Date.now()}`;
  const processedContent = isUser
    ? content
    : linkifyCitations(content, messageId);

  return (
    <div
      className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? "bg-ink" : "bg-brand"
        }`}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>

      {/* Message bubble */}
      <div className={`max-w-[75%] ${isUser ? "text-right" : ""}`}>
        <div
          className={`rounded px-4 py-3 ${
            isUser
              ? "bg-brand text-white"
              : "bg-surface border border-line text-content"
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap text-sm">{content}</p>
          ) : (
            <div className="prose prose-sm max-w-none prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:font-heading prose-headings:text-ink prose-strong:text-ink">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  ...tableComponents,
                  a: ({ href, children, ...rest }) => {
                    // Chip de cita → scrollea a la fuente; tooltip con el
                    // texto citado del evento `citation`.
                    const num = citationNumberFromHref(href);
                    if (num != null) {
                      const source = sources?.[num - 1];
                      const tooltip =
                        source?.cited_text ||
                        source?.content_preview ||
                        source?.filename ||
                        undefined;
                      return (
                        <a
                          href={href}
                          onClick={handleCitationClick}
                          title={tooltip}
                          className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded-pill text-[11px] font-heading font-medium bg-energia text-ink hover:bg-ink hover:text-white no-underline transition-colors cursor-pointer align-middle"
                        >
                          {children}
                        </a>
                      );
                    }
                    return (
                      <a
                        href={href}
                        className="text-brand hover:text-brand-hover"
                        {...rest}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {processedContent}
              </ReactMarkdown>
            </div>
          )}

          {!isUser && sources && sources.length > 0 && (
            <ChatMessageSources sources={sources} messageId={messageId} />
          )}
        </div>

        <div
          className={`flex items-center gap-2 mt-1 ${
            isUser ? "justify-end" : ""
          }`}
        >
          {timestamp && (
            <span className="text-[11px] text-content-muted">
              {timestamp.toLocaleTimeString("es-ES", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          {!isUser && modelLabel(model) && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-pill bg-surface-200 text-content-muted"
              title={model ?? undefined}
            >
              {modelLabel(model)}
            </span>
          )}
          {!isUser && content && (
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(content)
                  .then(() => showToast.success("Respuesta copiada"))
                  .catch(() => showToast.error("No se pudo copiar"));
              }}
              title="Copiar respuesta"
              className="text-content-muted hover:text-ink transition-colors"
              aria-label="Copiar respuesta"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          )}
          {!isUser && traceId && (
            <span className="flex items-center gap-1">
              <button
                onClick={() => !sent && setVote("up")}
                disabled={sent}
                title="Respuesta útil"
                aria-label="Respuesta útil"
                className={`transition-colors disabled:cursor-default ${
                  vote === "up"
                    ? "text-brand"
                    : "text-content-muted hover:text-ink"
                }`}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => !sent && setVote("down")}
                disabled={sent}
                title="Respuesta incorrecta o incompleta"
                aria-label="Respuesta incorrecta"
                className={`transition-colors disabled:cursor-default ${
                  vote === "down"
                    ? "text-red-600"
                    : "text-content-muted hover:text-ink"
                }`}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
        </div>

        {!isUser && traceId && vote !== null && !sent && (
          <div className="mt-2 flex flex-col gap-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                vote === "down"
                  ? "¿Cuál sería la respuesta correcta? Comentario (opcional, ayuda al asistente a mejorar)"
                  : "Comentario (opcional)"
              }
              rows={2}
              className="w-full text-sm rounded border border-line bg-surface px-2 py-1 text-content focus:outline-none focus:border-brand"
            />
            <div className="flex gap-2">
              <button
                onClick={send}
                className="text-xs px-2 py-1 rounded bg-brand text-white hover:bg-brand-hover"
              >
                Enviar valoración
              </button>
              <button
                onClick={() => setVote(null)}
                className="text-xs px-2 py-1 rounded text-content-muted hover:text-ink"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
